import { createContext, useCallback, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isArabicAvailableFor, resolveStoredLanguage, StoredLanguage } from "@/lib/languageEligibility";
import { commerceDict } from "@/i18n/commerce";

export type Language = "en" | "ar";

interface LanguageSettings {
  language_code: string;
  language_name: string;
  native_name: string;
  is_enabled: boolean;
  is_default: boolean;
  direction: string;
  preview_mode: boolean;
  is_live: boolean;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
  isArabicEnabled: boolean;
  isArabicPreview: boolean;
  isArabicLive: boolean;
  t: (key: string) => string;
  getLocalizedField: <T>(enValue: T, arValue: T | null | undefined) => T;
  languageSettings: LanguageSettings[];
  loading: boolean;
  isAdmin: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = "anaya-language";

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>("en");
  const [languageSettings, setLanguageSettings] = useState<LanguageSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [failed, setFailed] = useState(false);
  // Generation counter: only the latest resolution may commit state, so a
  // slow earlier response can never overwrite a newer one.
  const resolveGeneration = useRef(0);

  // Resolve settings AND the caller's role together before enforcing any
  // preview eligibility. Previously settings loaded while isAdmin was still
  // false and a stored Arabic choice was erased before the admin role
  // resolved. Fail closed: any settings/role error means no Arabic.
  const resolve = useCallback(async () => {
    const generation = ++resolveGeneration.current;
    const [settingsResult, userResult] = await Promise.all([
      supabase
        .from("language_settings")
        .select("*")
        .order("display_order", { ascending: true }),
      supabase.auth.getUser(),
    ]);
    if (generation !== resolveGeneration.current) return; // superseded

    if (settingsResult.error || !settingsResult.data) {
      setFailed(true);
      setIsAdmin(false);
      setLanguageState("en");
      setLoading(false);
      return;
    }

    let admin = false;
    const user = userResult.data.user;
    if (user) {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .limit(1);
      if (generation !== resolveGeneration.current) return; // superseded
      if (error) {
        setFailed(true);
        setIsAdmin(false);
        setLanguageState("en");
        setLoading(false);
        return;
      }
      admin = (data?.length ?? 0) > 0;
    }

    const settings = settingsResult.data;
    const arabic = settings.find((l) => l.language_code === "ar");
    const available = isArabicAvailableFor({
      isLive: !!arabic?.is_live,
      previewMode: !!arabic?.preview_mode,
      isAdmin: admin,
    });
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as StoredLanguage;
    const { language: next, downgrade } = resolveStoredLanguage(stored, available);

    setFailed(false);
    setIsAdmin(admin);
    setLanguageSettings(settings);
    // Only the language key is touched here — quiz/cart storage is untouched.
    if (downgrade) localStorage.setItem(LANGUAGE_STORAGE_KEY, "en");
    setLanguageState(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    void resolve();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // Drop admin immediately on sign-out so the preview switcher and Arabic
      // disappear without waiting for the async re-check; resolve() then
      // clears any stored preview selection.
      if (event === "SIGNED_OUT") setIsAdmin(false);
      void resolve();
    });

    const channel = supabase
      .channel("language-settings-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "language_settings",
        },
        () => {
          void resolve();
        }
      )
      .subscribe();

    return () => {
      resolveGeneration.current += 1; // invalidate in-flight work
      subscription.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [resolve]);

  // Apply RTL/LTR to document
  useEffect(() => {
    const dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
    
    // Add/remove RTL class for Tailwind
    if (language === "ar") {
      document.documentElement.classList.add("rtl");
    } else {
      document.documentElement.classList.remove("rtl");
    }
  }, [language]);

  const arabicSetting = languageSettings.find((l) => l.language_code === "ar");
  const isArabicPreview = arabicSetting?.preview_mode || false;
  const isArabicLive = arabicSetting?.is_live || false;
  
  // Arabic is enabled if it's live OR if it's in preview mode and user is
  // admin. While loading, settings are empty so this stays false; a failed
  // resolution also fails closed.
  const isArabicEnabled = !failed && (isArabicLive || (isArabicPreview && isAdmin));

  const setLanguage = (lang: Language) => {
    // Only allow Arabic if it's available for this user
    if (lang === "ar" && !isArabicEnabled) {
      return;
    }
    
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  };

  const isRTL = language === "ar";

  // Translation function
  const t = (key: string): string => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  // Helper to get localized field with fallback
  const getLocalizedField = <T,>(enValue: T, arValue: T | null | undefined): T => {
    if (language === "ar" && arValue !== null && arValue !== undefined && arValue !== "") {
      return arValue;
    }
    return enValue;
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        isRTL,
        isArabicEnabled,
        isArabicPreview,
        isArabicLive,
        t,
        getLocalizedField,
        languageSettings,
        loading,
        isAdmin,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

// Static translations for UI text
// Exported for tests that verify EN/AR key parity.
export const translations: Record<Language, Record<string, string>> = {
  en: {
    ...commerceDict.en,
    // Navigation
    "nav.home": "Home",
    "nav.howItWorks": "How It Works",
    "nav.createYourScent": "Create Your Scent",
    "nav.aboutAnaya": "About Anaya",
    "nav.anayaExperience": "Anaya Experience",
    "nav.blog": "Blog",
    "nav.contact": "Contact Us",
    "nav.returnsRefunds": "Returns & Refunds",
    "nav.privacyPolicy": "Privacy Policy",
    "nav.termsConditions": "Terms & Conditions",
    "legal.termsSubtitle": "Please read these terms carefully before using our services.",
    "legal.privacySubtitle": "Your privacy matters to us.",
    "legal.returnsSubtitle": "Our commitment to your satisfaction.",
    "nav.whoWeAre": "Who We Are",
    "nav.originOfAnaya": "The Origin of Anaya",
    "nav.essenceOfAnaya": "The Essence of Anaya",
    "nav.missionVision": "Our Mission & Vision",
    "nav.sustainability": "Sustainability",
    "nav.specialOccasions": "Souvenirs for Special Occasions",
    "nav.corporateGifting": "Corporate Gifting & B2B Events",
    "nav.reorderPerfume": "Reorder Your Perfume",
    "nav.collection": "Collection",

    // Buttons & CTAs
    "btn.shopNow": "Shop Now",
    "btn.addToCart": "Add to Cart",
    "btn.buyNow": "Buy Now",
    "btn.contactUs": "Contact Us",
    "btn.learnMore": "Learn More",
    "btn.viewDetails": "View Details",
    "btn.submit": "Submit",
    "btn.save": "Save",
    "btn.cancel": "Cancel",
    "btn.confirm": "Confirm",
    "btn.delete": "Delete",
    "btn.edit": "Edit",
    "btn.back": "Back",
    "btn.next": "Next",
    "btn.previous": "Previous",
    "btn.close": "Close",
    "btn.apply": "Apply",
    "btn.clear": "Clear",
    "btn.search": "Search",
    "btn.filter": "Filter",
    "btn.sort": "Sort",
    "btn.refresh": "Refresh",
    "btn.returnHome": "Return Home",
    "btn.createYourScent": "Create Your Scent",
    "btn.designYourGift": "Design Your Gift",
    "btn.readMore": "Read More",
    "btn.readLess": "Read Less",
    "btn.proceedToCheckout": "Proceed to Checkout",
    "btn.continueShopping": "Continue Shopping",
    "btn.placeOrder": "Place Order",
    "btn.trackOrder": "Track Order",
    "btn.viewOrders": "View Orders",
    "btn.signIn": "Sign In",
    "btn.signUp": "Sign Up",
    "btn.signOut": "Sign Out",
    "btn.myAccount": "My Account",
    "btn.viewCart": "View Cart",

    // Cart & Checkout
    "cart.title": "Shopping Bag",
    "cart.empty": "Your bag is empty",
    "cart.subtotal": "Subtotal",
    "cart.shipping": "Shipping",
    "cart.tax": "Tax",
    "cart.total": "Total",
    "cart.quantity": "Quantity",
    "cart.remove": "Remove",
    "checkout.title": "Checkout",
    "checkout.shippingAddress": "Shipping Address",
    "checkout.paymentMethod": "Payment Method",
    "checkout.orderSummary": "Order Summary",
    "checkout.placeOrder": "Place Order",

    // Forms
    "form.firstName": "First Name",
    "form.lastName": "Last Name",
    "form.email": "Email",
    "form.phone": "Phone",
    "form.address": "Address",
    "form.city": "City",
    "form.state": "State",
    "form.postalCode": "Postal Code",
    "form.country": "Country",
    "form.message": "Message",
    "form.required": "Required",
    "form.optional": "Optional",

    // Product
    "product.addToCart": "Add to Cart",
    "product.outOfStock": "Out of Stock",
    "product.inStock": "In Stock",
    "product.description": "Description",
    "product.details": "Details",
    "product.reviews": "Reviews",
    "product.relatedProducts": "Related Products",
    "product.topNotes": "Top Notes",
    "product.heartNotes": "Heart Notes",
    "product.baseNotes": "Base Notes",
    "product.fragrance": "Fragrance",
    "product.size": "Size",
    "product.price": "Price",

    // Account
    "account.title": "My Account",
    "account.orders": "Orders",
    "account.addresses": "Addresses",
    "account.settings": "Settings",
    "account.wishlist": "Wishlist",
    "account.credits": "Credits",
    "account.returns": "Returns",

    // Common
    "common.loading": "Loading...",
    "common.error": "An error occurred",
    "common.success": "Success",
    "common.noResults": "No results found",
    "common.all": "All",
    "common.or": "or",
    "common.and": "and",
    "common.yes": "Yes",
    "common.no": "No",
    "common.free": "Free",

    // Footer
    "footer.allRightsReserved": "All rights reserved",
    "install.cta": "Add ANAYA to your Home Screen",
    "install.title": "Add ANAYA to your Home Screen",
    "install.intro": "Open ANAYA straight from an icon on your phone. Nothing is downloaded from an app store.",
    "install.installNow": "Add to Home Screen",
    "install.dismissed": "No problem — you can add ANAYA any time from this button.",
    "install.failed": "Your browser couldn't show the install option. Please use the steps below.",
    "install.iosTitle": "iPhone or iPad (Safari)",
    "install.ios1": "Tap the Share button (square with an arrow) in Safari.",
    "install.ios2": "Scroll down and tap \"Add to Home Screen\".",
    "install.ios3": "Tap \"Add\" in the top corner.",
    "install.androidTitle": "Android (Chrome)",
    "install.android1": "Tap the menu (three dots) at the top of Chrome.",
    "install.android2": "Tap \"Add to Home screen\" or \"Install app\".",
    "install.android3": "Confirm by tapping \"Add\" or \"Install\".",
    "install.inAppTitle": "Opened from Instagram, WhatsApp or another app?",
    "install.inAppText": "These apps open links in their own browser, which can't add icons. Tap the menu (… or ⋮) and choose \"Open in Safari\" or \"Open in Chrome\", then follow the steps above.",
    "install.close": "Close",
    "footer.quickLinks": "Quick Links",
    "footer.followUs": "Follow Us",
    "footer.newsletter": "Newsletter",
    "footer.subscribeText": "Subscribe to receive updates and exclusive offers",
    "footer.enterEmail": "Enter your email",
    "footer.subscribe": "Subscribe",

    // 404
    "notFound.title": "Page Not Found",
    "notFound.message": "The page you're looking for seems to have wandered off like a fleeting scent.",

    // Quiz
    "quiz.title": "Create Your Scent",
    "quiz.subtitle": "Answer a few questions to discover your perfect fragrance",
    "quiz.step": "Step",
    "quiz.of": "of",
    "quiz.recipientName": "Recipient Name",
    "quiz.gender": "Gender",
    "quiz.age": "Age",
    "quiz.skinType": "Skin Type",
    "quiz.nature": "Nature",
    "quiz.personality": "Personality",
    "quiz.style": "Style",
    "quiz.specialRequests": "Special Requests",

    // Tooltips
    "tooltip.cart": "Shopping Bag",
    "tooltip.whatsapp": "Chat on WhatsApp",
    "tooltip.collection": "Anaya Collection",

    // Shared chrome (header, drawer, account menu)
    "account.menu": "Account menu",
    "account.myProfile": "My Profile",
    "account.myOrders": "My Orders",
    "account.myAddresses": "My Addresses",
    "account.invoices": "Invoices",
    "account.contactAnaya": "Contact ANAYA",
    "account.welcomeGuest": "Welcome, Guest",
    "account.signInRegister": "Sign In / Register",
    "account.reorderByCode": "Reorder by Code",
    "account.customer": "Customer",
    "account.active": "active",
    "header.toggleMenu": "Toggle menu",
    "header.closeMenu": "Close menu",
    "header.mainMenu": "Main menu",
    "lang.switchToArabic": "Switch to Arabic",
    "lang.switchToEnglish": "Switch to English",
    "common.preview": "Preview",

    // Homepage sections (fallbacks when a section record has no text)
    "home.theExperience": "The Experience",
    "home.step1Title": "Take the Quiz",
    "home.step1Description": "Share your personality, nature preferences, and style to help our AI understand your essence.",
    "home.step2Title": "AI Creates Your Scent",
    "home.step2Description": "Our AI curates three unique perfumes tailored for different moments in your life.",
    "home.step3Title": "Receive Your Creation",
    "home.step3Description": "Your personalized fragrances arrive in luxurious packaging, ready to tell your story.",
    "home.threeFragrances": "Three Fragrances",
    "home.forEveryMoment": "For Every Moment",
    "home.forEveryMomentDescription": "Each creation includes three distinct perfumes designed for your lifestyle.",
    "home.completeExperience": "The Complete Experience",
    "home.signatureBox": "The Signature Box",
    "home.corporateExcellence": "Corporate Excellence",
    "home.explore": "Explore",
    "home.beginYourJourney": "Begin Your Journey",

    // Information & contact pages
    "common.comingSoon": "Content coming soon...",
    "form.subject": "Subject",
    "contact.callUs": "Call us",
    "contact.thankYou": "Thank you for reaching out. Anaya will get back to you soon.",
    "contact.messageSent": "Your message has been accepted by our email service for sending to ANAYA. Thank you for reaching out.",
    "contact.sendFailed": "Could not send message",
    "contact.tryAgain": "Please try again or email us directly.",
    "contact.acceptanceUnknown": "We could not confirm whether your message was accepted. Check its status before sending again to avoid a duplicate, or email us directly.",
    "contact.checkStatus": "Check message status",
    "contact.sendAnother": "Send Another Message",

    // Journal / blog, gallery and media
    "blog.eyebrow": "Stories & Insights",
    "blog.title": "The ANAYA Journal",
    "blog.intro": "Discover stories, tips, and insights from the world of personalized fragrances. Explore the art of scent, the science behind our creations, and the emotions they evoke.",
    "blog.videos": "Videos",
    "blog.gallery": "Gallery",
    "blog.articles": "Articles",
    "blog.readArticle": "Read Article",
    "blog.readLess": "Read Less",
    "blog.comingSoon": "Coming Soon",
    "blog.videoPlaceholder": "Video",
    "blog.videosEmpty": "Video content coming soon. Stay tuned for behind-the-scenes looks, tutorials, and more.",
    "blog.articlesEmpty": "Articles coming soon. Check back for stories about fragrance, wellness, and lifestyle.",
    "blog.categoryFallback": "Article",
    "blog.previousArticle": "Previous Article",
    "blog.nextArticle": "Next Article",
    "blog.backToBlog": "Back to Blog",
    "blog.playVideo": "Play video",
    "blog.videoThumbnailAlt": "Video thumbnail",
    "blog.ctaTitle": "Ready to Discover Your Signature Scent?",
    "blog.ctaDescription": "Let our AI-powered quiz guide you to a fragrance that's uniquely yours.",

    // Anaya Collection (public product display)
    "collection.exclusiveCollection": "Exclusive Collection",
    "collection.heroTitle": "Anaya Collection",
    "collection.heroSubtitle": "Discover our curated selection of premium fragrances, crafted for those who appreciate the art of perfumery.",
    "collection.allFragrances": "All Fragrances",
    "collection.showingOf": "Showing {shown} of {total} products",
    "collection.loadingMore": "Loading more...",
    "collection.noProducts": "No products yet",
    "collection.noProductsFiltered": "No products match your current filters. Try adjusting your search criteria.",
    "collection.noProductsEmpty": "Our collection is being curated. Check back soon for amazing fragrances!",
    "collection.clearAllFilters": "Clear all filters",
    "collection.comingSoon": "Coming Soon",
    "collection.stayUpdated": "Stay Updated",
    "collection.stayUpdatedText": "Be the first to know about new arrivals, exclusive offers, and special collections.",
    "collection.subscribeThanks": "Thank you for subscribing!",
    "collection.filters": "Filters",
    "collection.searchPlaceholder": "Search perfumes, notes, styles...",
    "collection.clearAll": "Clear all",
    "collection.fragranceCount": "{count} fragrances",
    "collection.showingFragrances": "Showing {shown} of {total} fragrances",
    "collection.selected": "{count} selected",
    "collection.noOptions": "No options found",
    "collection.applyFilters": "Apply Filters",
    "collection.searchValues": "Search {label}...",
    "collection.badgeNew": "New",
    "collection.badgeBestseller": "Bestseller",
    "collection.badgeLimited": "Limited",
    "collection.badgeSignature": "Signature",
    "collection.sampleInCart": "Sample in Cart (1)",
    "collection.sampleLimitReached": "Sample limit reached",
    "collection.tryFreeSample": "Try Free Sample",
    "collection.reviewsCount": "Reviews ({count})",
    "collection.reviewCount": "{count} reviews",
    "collection.writeReview": "Write a Review",
    "collection.yourRating": "Your Rating",
    "collection.reviewTitle": "Review Title",
    "collection.reviewTitlePlaceholder": "Summarize your experience",
    "collection.yourReview": "Your Review",
    "collection.reviewPlaceholder": "Tell us about your experience with this fragrance...",
    "collection.submitReview": "Submit Review",
    "collection.submitting": "Submitting...",
    "collection.noReviewsYet": "No reviews yet. Be the first to review this product!",
    "collection.verifiedPurchase": "Verified Purchase",
    "collection.helpful": "Helpful ({count})",
    "collection.selectSize": "Select Size",
    "collection.adding": "Adding...",
    "collection.alreadyInCart": "Already in Cart ({count})",
    "collection.shippingPolicy": "Shipping Policy",
    "collection.returnPolicy": "Return Policy",
    "collection.securePayment": "Secure Payment",
    "collection.poweredByStripe": "powered by Stripe",
    "collection.fragranceDetails": "Fragrance Details",
    "collection.accord": "Accord",
    "collection.fragranceFamily": "Fragrance Family",
    "collection.family": "Family",
    "collection.occasion": "Occasion",
    "collection.occasionToWear": "Occasion to Wear",
    "collection.whenToWear": "When to Wear",
    "collection.itemCode": "Item Code",
    "collection.fragranceNotes": "Fragrance Notes",
    "collection.topNotesDesc": "First impression, lasting 15-30 minutes",
    "collection.heartNotesDesc": "The heart, lasting 2-4 hours",
    "collection.baseNotesDesc": "The foundation, lasting 6+ hours",
    "collection.noTopNotes": "No top notes assigned",
    "collection.noHeartNotes": "No heart notes assigned",
    "collection.noBaseNotes": "No base notes assigned",
    "collection.noteOrigin": "Origin",
    "collection.noDescription": "No description available.",
    "collection.noNotesAvailable": "No fragrance notes available for this product.",
    "collection.relatedTitle": "You May Also Like",
    "collection.noImage": "No Image",
    "collection.noImageAvailable": "No image available",
    "collection.similarAccordsTitle": "Similar Accord Perfumes",
    "collection.similarAccordsText": "This fragrance shares characteristics with these renowned perfumes:",
    "collection.showLess": "Show Less",
    "collection.readMoreCount": "Read More ({count} more)",
    "collection.includedPerfumes": "Included Perfumes",
    "collection.bottles": "{count} bottles",
    "collection.productNotFound": "Product Not Found",
    "collection.productNotFoundText": "The fragrance you're looking for doesn't exist or is no longer available.",
    "collection.browseCollection": "Browse the Collection",
    "collection.freeSamplesUsed": "Free samples ({used}/4 used)",
    "collection.sampleLimit4": "You have reached the limit of 4 free samples.",
    "collection.genderFragrance": "{gender}'s Fragrance",
    "collection.previousImage": "Previous image",
    "collection.nextImage": "Next image",
    "collection.shippingInfo": "Shipping Information",
    "collection.uaeDelivery": "UAE Delivery",
    "collection.freeShippingOver": "Free shipping on orders over AED 200",
    "collection.estimatedDelivery": "Estimated Delivery",
    "collection.deliveryTime": "2-5 business days within UAE",
    "collection.securePackaging": "Secure Packaging",
    "collection.packagingText": "All orders are carefully packaged",
    "collection.returns14": "14-Day Returns",
    "collection.returns14Text": "Return unopened items within 14 days",
    "collection.easyRefunds": "Easy Refunds",
    "collection.easyRefundsText": "Full refund for eligible returns",
    "collection.signInRequired": "Please sign in",
    "collection.signInToCart": "You need to be logged in to add items to your cart.",
    "collection.sampleMaxTitle": "Maximum quantity reached",
    "collection.sampleMaxDesc": "You can only add 1 sample per product.",
    "collection.sampleQtyLimited": "Sample quantity limited",
    "collection.sampleQtyDesc": "Only 1 sample is allowed per product.",
    "collection.sample": "Sample",
    "collection.reviewNotAllowed": "Review not allowed",
    "collection.reviewPurchasedOnly": "You can only review products you have purchased.",
    "collection.noEligibleOrder": "No eligible order",
    "collection.shippingPolicyUnavailable": "Shipping policy details are not yet available.",
    "collection.returnPolicyUnavailable": "Return policy details are not yet available.",

    // Quiz journey (original quiz, results, package selection, personalization)
    "quiz.discoverEssence": "Discover Your Essence",
    "quiz.createSignature": "Create Your Signature Scent",
    "quiz.stepName.question": "Who is this fragrance for?",
    "quiz.stepName.subtitle": "Enter the name of the person who will wear this scent",
    "quiz.stepGender.question": "Select the gender preference",
    "quiz.stepGender.subtitle": "This helps us tailor the fragrance profile",
    "quiz.stepAge.question": "What's the age bracket?",
    "quiz.stepAge.subtitle": "Different ages call for different notes",
    "quiz.stepSkin.question": "What's the skin type?",
    "quiz.stepSkin.subtitle": "Skin chemistry affects how fragrances develop",
    "quiz.stepNature.question": "Which natural environment speaks to the soul?",
    "quiz.stepNature.subtitle": "Nature inspires the essence of your fragrance",
    "quiz.stepPersonality.question": "Describe the personality style",
    "quiz.stepPersonality.subtitle": "Your essence defines your scent",
    "quiz.stepStyle.question": "Preferred fragrance style",
    "quiz.stepStyle.subtitle": "The tradition that guides your creation",
    "quiz.namePlaceholder": "Enter recipient name...",
    "quiz.countryPlaceholder": "Select shipping country *",
    "quiz.countryLocked": "Country is locked to match your first order",
    "quiz.continue": "Continue",
    "quiz.cancelEdit": "Cancel Edit",
    "quiz.stepOf": "STEP {current} of {total}",
    "quiz.resultsSaved": "Your results are saved",
    "quiz.codesError": "We couldn't create your perfume codes. Try again to continue with your personalized order.",
    "quiz.tryAgain": "Try again",
    "quiz.tryingAgain": "Trying again…",
    "quiz.backToQuiz": "Back to Quiz",
    "quiz.createdFor": "Created for {name}",
    "quiz.aiCollection": "Your AI-Crafted Collection",
    "quiz.openingFallback": "Based on your {personality} personality and love for {nature}, we've composed three unique fragrances in the {style} tradition.",
    "quiz.selectPackage": "Select Your Package",
    "quiz.bestValue": "Best Value",
    "quiz.selectPerfume": "Select Your Perfume",
    "quiz.packageFallback": "Premium AI-created perfume.",
    "quiz.viewPackageInfo": "View info about {name}",
    "quiz.editSelection": "Edit Your Selection",
    "quiz.chooseExperience": "Choose Your Experience",
    "quiz.loadingPackages": "Loading packages...",
    "quiz.saveChanges": "Save Changes",
    "quiz.selectPackageError": "Select a Package",
    "quiz.makeItPersonal": "Make It Personal",
    "quiz.personalTouch": "Add Your Personal Touch",
    "quiz.personalIntro": "Create a memorable gift experience with a heartfelt message.",
    "quiz.personalIntroBox": "Create a memorable gift experience with a heartfelt message and optional photo.",
    "quiz.greetingCard": "Greeting Card Message",
    "quiz.messageLabel": "Write a personal message (optional)",
    "quiz.messagePlaceholder": "Dear [Name], this fragrance was created especially for you...",
    "quiz.rephrase": "Rephrase",
    "quiz.rephrasing": "Rephrasing…",
    "quiz.toArabic": "To Arabic",
    "quiz.toEnglish": "To English",
    "quiz.translating": "Translating…",
    "quiz.characters": "{count}/500 characters",
    "quiz.photoTitle": "Photo Personalization",
    "quiz.photoSubtitle": "Upload a photo to be featured on your Signature Box lid (free).",
    "quiz.uploading": "Uploading...",
    "quiz.preservingQuality": "Preserving full quality",
    "quiz.clickToUpload": "Click to upload",
    "quiz.photoFormats": "PNG, JPG up to 20MB",
    "quiz.photoHere": "Your photo will appear here",
    "quiz.fullResolution": "Full resolution preserved for print",
    "quiz.choosePackage": "Choose Your Package",
    "quiz.editing": "Editing",
    "quiz.phase.quiz": "Quiz",
    "quiz.phase.package": "Package",
    "quiz.phase.personalization": "Personalization",
    "quiz.phase.checkout": "Checkout",
    "quiz.phase.confirmation": "Confirmation",
    "quiz.signatureBlend": "Signature Blend",
    "quiz.perfumeCode": "Your Perfume Code",
    "quiz.generating": "Generating...",
    "quiz.topNotes": "Top Notes",
    "quiz.heartNotes": "Heart Notes",
    "quiz.baseNotes": "Base Notes",
    "quiz.noteTop": "Top",
    "quiz.noteHeart": "Heart",
    "quiz.noteBase": "Base",
    "quiz.selected": "Selected",
    "quiz.typeSensation": "Sensation (Formal)",
    "quiz.typeFreshEnergy": "Fresh Energy (Outdoor)",
    "quiz.typeCasual": "Casual (Everyday)",
    "quiz.loadingTitle": "Crafting Your Signature Scents",
    "quiz.loadingSubtitle": "Our AI is analyzing your preferences and composing three unique fragrances just for {name}...",
    "quiz.tapToPlay": "Tap to play",
    "quiz.clickToPlay": "Click to play",
    "quiz.fileTooLarge": "File too large",
    "quiz.fileTooLargeDesc": "Please select an image under 20MB",
    "quiz.uploadFailed": "Upload failed",
    "quiz.uploadFailedDesc": "Please try a different image.",
    "quiz.couldntRephrase": "Couldn't rephrase",
    "quiz.rephraseRetry": "Please try again in a moment.",
    "quiz.rephrased": "Message rephrased",
    "quiz.rephrasedDesc": "Your message has been polished with a warmer touch.",
    "quiz.couldntTranslate": "Couldn't translate",
    "quiz.translatedAr": "Translated to Arabic",
    "quiz.translatedArDesc": "Your message has been translated.",
    "quiz.translatedEn": "Translated to English",
    "quiz.translatedEnDesc": "Your message has been translated.",
    "quiz.sessionExpired": "Session Expired",
    "quiz.sessionExpiredDesc": "Please start the quiz again.",
    "quiz.orderSaved": "Order Saved",
    "quiz.orderSavedBox": "Signature Box saved to cart.",
    "quiz.orderSavedSingle": "Single Bottle saved to cart.",
    "quiz.couldntSaveOrder": "Couldn't save order",
    "quiz.storageFullDesc": "Your browser storage is full. Please try again after clearing the cart.",
    "quiz.couldntSaveChanges": "Couldn't save changes",
    "quiz.photoTooLargeDesc": "The uploaded photo is too large to store. Please upload a smaller photo and try again.",
    "quiz.itemUpdated": "Item Updated",
    "quiz.itemUpdatedDesc": "Your cart item has been updated.",
    "quiz.tryAgainDesc": "Please try again.",
    "quiz.somethingWrong": "Something went wrong. Please try again.",
  },
  ar: {
    ...commerceDict.ar,
    // Navigation
    "nav.home": "الرئيسية",
    "nav.howItWorks": "كيف يعمل",
    "nav.createYourScent": "اصنع عطرك",
    "nav.aboutAnaya": "عن عنايا",
    "nav.anayaExperience": "تجربة عنايا",
    "nav.blog": "المدونة",
    "nav.contact": "اتصل بنا",
    "nav.returnsRefunds": "الإرجاع والاسترداد",
    "nav.privacyPolicy": "سياسة الخصوصية",
    "nav.termsConditions": "الشروط والأحكام",
    "legal.termsSubtitle": "يرجى قراءة هذه الشروط بعناية قبل استخدام خدماتنا.",
    "legal.privacySubtitle": "خصوصيتك تهمنا.",
    "legal.returnsSubtitle": "التزامنا برضاكم.",
    "nav.whoWeAre": "من نحن",
    "nav.originOfAnaya": "أصل عنايا",
    "nav.essenceOfAnaya": "جوهر عنايا",
    "nav.missionVision": "رسالتنا ورؤيتنا",
    "nav.sustainability": "الاستدامة",
    "nav.specialOccasions": "هدايا للمناسبات الخاصة",
    "nav.corporateGifting": "الهدايا المؤسسية وفعاليات الشركات",
    "nav.reorderPerfume": "أعد طلب عطرك",
    "nav.collection": "المجموعة",

    // Buttons & CTAs
    "btn.shopNow": "تسوق الآن",
    "btn.addToCart": "أضف إلى السلة",
    "btn.buyNow": "اشتر الآن",
    "btn.contactUs": "اتصل بنا",
    "btn.learnMore": "اعرف المزيد",
    "btn.viewDetails": "عرض التفاصيل",
    "btn.submit": "إرسال",
    "btn.save": "حفظ",
    "btn.cancel": "إلغاء",
    "btn.confirm": "تأكيد",
    "btn.delete": "حذف",
    "btn.edit": "تعديل",
    "btn.back": "رجوع",
    "btn.next": "التالي",
    "btn.previous": "السابق",
    "btn.close": "إغلاق",
    "btn.apply": "تطبيق",
    "btn.clear": "مسح",
    "btn.search": "بحث",
    "btn.filter": "تصفية",
    "btn.sort": "ترتيب",
    "btn.refresh": "تحديث",
    "btn.returnHome": "العودة للرئيسية",
    "btn.createYourScent": "اصنع عطرك",
    "btn.designYourGift": "صمّم هديتك",
    "btn.readMore": "اقرأ المزيد",
    "btn.readLess": "عرض أقل",
    "btn.proceedToCheckout": "المتابعة للدفع",
    "btn.continueShopping": "متابعة التسوق",
    "btn.placeOrder": "إتمام الطلب",
    "btn.trackOrder": "تتبع الطلب",
    "btn.viewOrders": "عرض الطلبات",
    "btn.signIn": "تسجيل الدخول",
    "btn.signUp": "إنشاء حساب",
    "btn.signOut": "تسجيل الخروج",
    "btn.myAccount": "حسابي",
    "btn.viewCart": "عرض السلة",

    // Cart & Checkout
    "cart.title": "سلة التسوق",
    "cart.empty": "سلتك فارغة",
    "cart.subtotal": "المجموع الفرعي",
    "cart.shipping": "الشحن",
    "cart.tax": "الضريبة",
    "cart.total": "الإجمالي",
    "cart.quantity": "الكمية",
    "cart.remove": "إزالة",
    "checkout.title": "إتمام الشراء",
    "checkout.shippingAddress": "عنوان الشحن",
    "checkout.paymentMethod": "طريقة الدفع",
    "checkout.orderSummary": "ملخص الطلب",
    "checkout.placeOrder": "إتمام الطلب",

    // Forms
    "form.firstName": "الاسم الأول",
    "form.lastName": "اسم العائلة",
    "form.email": "البريد الإلكتروني",
    "form.phone": "الهاتف",
    "form.address": "العنوان",
    "form.city": "المدينة",
    "form.state": "المنطقة",
    "form.postalCode": "الرمز البريدي",
    "form.country": "الدولة",
    "form.message": "الرسالة",
    "form.required": "مطلوب",
    "form.optional": "اختياري",

    // Product
    "product.addToCart": "أضف إلى السلة",
    "product.outOfStock": "غير متوفر",
    "product.inStock": "متوفر",
    "product.description": "الوصف",
    "product.details": "التفاصيل",
    "product.reviews": "التقييمات",
    "product.relatedProducts": "منتجات ذات صلة",
    "product.topNotes": "مقدمة العطر",
    "product.heartNotes": "قلب العطر",
    "product.baseNotes": "قاعدة العطر",
    "product.fragrance": "العطر",
    "product.size": "الحجم",
    "product.price": "السعر",

    // Account
    "account.title": "حسابي",
    "account.orders": "الطلبات",
    "account.addresses": "العناوين",
    "account.settings": "الإعدادات",
    "account.wishlist": "قائمة الأمنيات",
    "account.credits": "الرصيد",
    "account.returns": "المرتجعات",

    // Common
    "common.loading": "جاري التحميل...",
    "common.error": "حدث خطأ",
    "common.success": "تم بنجاح",
    "common.noResults": "لا توجد نتائج",
    "common.all": "الكل",
    "common.or": "أو",
    "common.and": "و",
    "common.yes": "نعم",
    "common.no": "لا",
    "common.free": "مجاني",

    // Footer
    "footer.allRightsReserved": "جميع الحقوق محفوظة",
    "install.cta": "أضف أنايا إلى شاشتك الرئيسية",
    "install.title": "أضف أنايا إلى شاشتك الرئيسية",
    "install.intro": "افتح أنايا مباشرة من أيقونة على هاتفك. لا يتم تنزيل أي شيء من متجر التطبيقات.",
    "install.installNow": "أضف إلى الشاشة الرئيسية",
    "install.dismissed": "لا مشكلة — يمكنك إضافة أنايا في أي وقت من هذا الزر.",
    "install.failed": "لم يتمكن متصفحك من عرض خيار التثبيت. يرجى اتباع الخطوات أدناه.",
    "install.iosTitle": "آيفون أو آيباد (سفاري)",
    "install.ios1": "اضغط على زر المشاركة (مربع به سهم) في سفاري.",
    "install.ios2": "مرّر للأسفل واضغط على \"إضافة إلى الشاشة الرئيسية\".",
    "install.ios3": "اضغط على \"إضافة\" في الزاوية العلوية.",
    "install.androidTitle": "أندرويد (كروم)",
    "install.android1": "اضغط على القائمة (ثلاث نقاط) أعلى كروم.",
    "install.android2": "اضغط على \"إضافة إلى الشاشة الرئيسية\" أو \"تثبيت التطبيق\".",
    "install.android3": "أكّد بالضغط على \"إضافة\" أو \"تثبيت\".",
    "install.inAppTitle": "هل فتحت الموقع من إنستغرام أو واتساب أو تطبيق آخر؟",
    "install.inAppText": "تفتح هذه التطبيقات الروابط في متصفحها الخاص الذي لا يمكنه إضافة أيقونات. اضغط على القائمة (… أو ⋮) واختر \"الفتح في سفاري\" أو \"الفتح في كروم\"، ثم اتبع الخطوات أعلاه.",
    "install.close": "إغلاق",
    "footer.quickLinks": "روابط سريعة",
    "footer.followUs": "تابعنا",
    "footer.newsletter": "النشرة الإخبارية",
    "footer.subscribeText": "اشترك لتلقي التحديثات والعروض الحصرية",
    "footer.enterEmail": "أدخل بريدك الإلكتروني",
    "footer.subscribe": "اشتراك",

    // 404
    "notFound.title": "الصفحة غير موجودة",
    "notFound.message": "يبدو أن الصفحة التي تبحث عنها قد اختفت كعطر عابر.",

    // Quiz
    "quiz.title": "اصنع عطرك",
    "quiz.subtitle": "أجب على بعض الأسئلة لاكتشاف عطرك المثالي",
    "quiz.step": "الخطوة",
    "quiz.of": "من",
    "quiz.recipientName": "اسم المستلم",
    "quiz.gender": "الجنس",
    "quiz.age": "العمر",
    "quiz.skinType": "نوع البشرة",
    "quiz.nature": "الطبيعة",
    "quiz.personality": "الشخصية",
    "quiz.style": "الأسلوب",
    "quiz.specialRequests": "طلبات خاصة",

    // Tooltips
    "tooltip.cart": "سلة التسوق",
    "tooltip.whatsapp": "تحدث عبر واتساب",
    "tooltip.collection": "مجموعة عنايا",

    // Shared chrome (header, drawer, account menu)
    "account.menu": "قائمة الحساب",
    "account.myProfile": "ملفي الشخصي",
    "account.myOrders": "طلباتي",
    "account.myAddresses": "عناويني",
    "account.invoices": "الفواتير",
    "account.contactAnaya": "تواصل مع أنايا",
    "account.welcomeGuest": "مرحباً بك",
    "account.signInRegister": "تسجيل الدخول / إنشاء حساب",
    "account.reorderByCode": "إعادة الطلب بالرمز",
    "account.customer": "عميل",
    "account.active": "نشط",
    "header.toggleMenu": "فتح أو إغلاق القائمة",
    "header.closeMenu": "إغلاق القائمة",
    "header.mainMenu": "القائمة الرئيسية",
    "lang.switchToArabic": "التبديل إلى العربية",
    "lang.switchToEnglish": "التبديل إلى الإنجليزية",
    "common.preview": "معاينة",

    // Homepage sections (fallbacks when a section record has no text)
    "home.theExperience": "التجربة",
    "home.step1Title": "أجب عن الأسئلة",
    "home.step1Description": "شاركنا شخصيتك وتفضيلاتك الطبيعية وأسلوبك، ليساعد ذلك الذكاء الاصطناعي على فهم جوهرك.",
    "home.step2Title": "الذكاء الاصطناعي يبتكر عطرك",
    "home.step2Description": "يختار الذكاء الاصطناعي ثلاثة عطور فريدة تناسب اللحظات المختلفة في حياتك.",
    "home.step3Title": "استلم إبداعك العطري",
    "home.step3Description": "تصلك عطورك الشخصية في تغليف فاخر، جاهزة لتحكي قصتك.",
    "home.threeFragrances": "ثلاثة عطور",
    "home.forEveryMoment": "لكل لحظة",
    "home.forEveryMomentDescription": "تضم كل مجموعة ثلاثة عطور مختلفة تناسب أسلوب حياتك.",
    "home.completeExperience": "التجربة المتكاملة",
    "home.signatureBox": "صندوق العطور المميز",
    "home.corporateExcellence": "تميّز الشركات",
    "home.explore": "استكشف",
    "home.beginYourJourney": "ابدأ رحلتك",

    // Information & contact pages
    "common.comingSoon": "المحتوى قادم قريباً...",
    "form.subject": "الموضوع",
    "contact.callUs": "اتصل بنا",
    "contact.thankYou": "شكراً لتواصلك معنا. سيتواصل معك فريق أنايا قريباً.",
    "contact.messageSent": "قبلت خدمة البريد الإلكتروني رسالتك لإرسالها إلى أنايا. شكراً لتواصلك معنا.",
    "contact.sendFailed": "تعذر إرسال الرسالة",
    "contact.tryAgain": "يرجى المحاولة مرة أخرى أو مراسلتنا عبر البريد الإلكتروني مباشرة.",
    "contact.acceptanceUnknown": "تعذر تأكيد قبول رسالتك. تحقق من حالتها قبل إعادة الإرسال لتجنب التكرار، أو راسلنا مباشرة عبر البريد الإلكتروني.",
    "contact.checkStatus": "تحقق من حالة الرسالة",
    "contact.sendAnother": "إرسال رسالة أخرى",

    // Journal / blog, gallery and media
    "blog.eyebrow": "قصص ورؤى",
    "blog.title": "مجلة أنايا",
    "blog.intro": "اكتشف قصصاً ونصائح ورؤى من عالم العطور الشخصية. استكشف فن العطور والعلم وراء إبداعاتنا والمشاعر التي تثيرها.",
    "blog.videos": "فيديوهات",
    "blog.gallery": "معرض الصور",
    "blog.articles": "مقالات",
    "blog.readArticle": "اقرأ المقال",
    "blog.readLess": "عرض أقل",
    "blog.comingSoon": "قريباً",
    "blog.videoPlaceholder": "فيديو",
    "blog.videosEmpty": "محتوى الفيديو قادم قريباً. ترقّب لقطات من الكواليس وشروحات والمزيد.",
    "blog.articlesEmpty": "المقالات قادمة قريباً. عد لاحقاً لقراءة قصص عن العطور والعافية ونمط الحياة.",
    "blog.categoryFallback": "مقال",
    "blog.previousArticle": "المقال السابق",
    "blog.nextArticle": "المقال التالي",
    "blog.backToBlog": "العودة إلى المدونة",
    "blog.playVideo": "تشغيل الفيديو",
    "blog.videoThumbnailAlt": "صورة مصغرة للفيديو",
    "blog.ctaTitle": "مستعد لاكتشاف عطرك المميز؟",
    "blog.ctaDescription": "دلّك اختبارنا المدعوم بالذكاء الاصطناعي إلى عطر يشبهك وحدك.",

    // Anaya Collection (public product display)
    "collection.exclusiveCollection": "مجموعة حصرية",
    "collection.heroTitle": "مجموعة أنايا",
    "collection.heroSubtitle": "اكتشف مجموعتنا المنتقاة من العطور الفاخرة، المصنوعة لمن يقدّر فن صناعة العطور.",
    "collection.allFragrances": "جميع العطور",
    "collection.showingOf": "عرض {shown} من {total} منتجاً",
    "collection.loadingMore": "جاري تحميل المزيد...",
    "collection.noProducts": "لا توجد منتجات بعد",
    "collection.noProductsFiltered": "لا توجد منتجات تطابق عوامل التصفية الحالية. جرّب تعديل معايير البحث.",
    "collection.noProductsEmpty": "يتم إعداد مجموعتنا حالياً. عد قريباً لاكتشاف عطور رائعة!",
    "collection.clearAllFilters": "مسح كل عوامل التصفية",
    "collection.comingSoon": "قريباً",
    "collection.stayUpdated": "ابق على اطلاع",
    "collection.stayUpdatedText": "كن أول من يعرف عن الوافدات الجديدة والعروض الحصرية والمجموعات الخاصة.",
    "collection.subscribeThanks": "شكراً لاشتراكك!",
    "collection.filters": "عوامل التصفية",
    "collection.searchPlaceholder": "ابحث عن العطور والنفحات والأنماط...",
    "collection.clearAll": "مسح الكل",
    "collection.fragranceCount": "{count} عطراً",
    "collection.showingFragrances": "عرض {shown} من {total} عطراً",
    "collection.selected": "{count} محدد",
    "collection.noOptions": "لا توجد خيارات",
    "collection.applyFilters": "تطبيق عوامل التصفية",
    "collection.searchValues": "ابحث في {label}...",
    "collection.badgeNew": "جديد",
    "collection.badgeBestseller": "الأكثر مبيعاً",
    "collection.badgeLimited": "إصدار محدود",
    "collection.badgeSignature": "مميز",
    "collection.sampleInCart": "العينة في السلة (1)",
    "collection.sampleLimitReached": "تم بلوغ حد العينات",
    "collection.tryFreeSample": "جرّب عينة مجانية",
    "collection.reviewsCount": "التقييمات ({count})",
    "collection.reviewCount": "{count} تقييماً",
    "collection.writeReview": "اكتب تقييماً",
    "collection.yourRating": "تقييمك",
    "collection.reviewTitle": "عنوان التقييم",
    "collection.reviewTitlePlaceholder": "لخّص تجربتك",
    "collection.yourReview": "تقييمك",
    "collection.reviewPlaceholder": "أخبرنا عن تجربتك مع هذا العطر...",
    "collection.submitReview": "إرسال التقييم",
    "collection.submitting": "جاري الإرسال...",
    "collection.noReviewsYet": "لا توجد تقييمات بعد. كن أول من يقيّم هذا المنتج!",
    "collection.verifiedPurchase": "عملية شراء موثقة",
    "collection.helpful": "مفيد ({count})",
    "collection.selectSize": "اختر الحجم",
    "collection.adding": "جاري الإضافة...",
    "collection.alreadyInCart": "موجود في السلة ({count})",
    "collection.shippingPolicy": "سياسة الشحن",
    "collection.returnPolicy": "سياسة الإرجاع",
    "collection.securePayment": "دفع آمن",
    "collection.poweredByStripe": "بدعم من Stripe",
    "collection.fragranceDetails": "تفاصيل العطر",
    "collection.accord": "التوافق العطري",
    "collection.fragranceFamily": "العائلة العطرية",
    "collection.family": "العائلة",
    "collection.occasion": "المناسبة",
    "collection.occasionToWear": "مناسبة الارتداء",
    "collection.whenToWear": "متى ترتديه",
    "collection.itemCode": "رمز الصنف",
    "collection.fragranceNotes": "نفحات العطر",
    "collection.topNotesDesc": "الانطباع الأول، يدوم 15-30 دقيقة",
    "collection.heartNotesDesc": "قلب العطر، يدوم 2-4 ساعات",
    "collection.baseNotesDesc": "الأساس، يدوم أكثر من 6 ساعات",
    "collection.noTopNotes": "لم تُسند نفحات عليا",
    "collection.noHeartNotes": "لم تُسند نفحات قلب",
    "collection.noBaseNotes": "لم تُسند نفحات قاعدة",
    "collection.noteOrigin": "الأصل",
    "collection.noDescription": "لا يوجد وصف متاح.",
    "collection.noNotesAvailable": "لا توجد نفحات عطرية متاحة لهذا المنتج.",
    "collection.relatedTitle": "قد يعجبك أيضاً",
    "collection.noImage": "لا توجد صورة",
    "collection.noImageAvailable": "لا توجد صورة متاحة",
    "collection.similarAccordsTitle": "عطور بتوافق مشابه",
    "collection.similarAccordsText": "يشترك هذا العطر في خصائص مع هذه العطور الشهيرة:",
    "collection.showLess": "عرض أقل",
    "collection.readMoreCount": "عرض المزيد ({count} إضافية)",
    "collection.includedPerfumes": "العطور المضمنة",
    "collection.bottles": "{count} زجاجات",
    "collection.productNotFound": "المنتج غير موجود",
    "collection.productNotFoundText": "العطر الذي تبحث عنه غير موجود أو لم يعد متاحاً.",
    "collection.browseCollection": "تصفح المجموعة",
    "collection.freeSamplesUsed": "عينات مجانية (تم استخدام {used}/4)",
    "collection.sampleLimit4": "لقد بلغت الحد الأقصى وهو 4 عينات مجانية.",
    "collection.genderFragrance": "عطر {gender}",
    "collection.previousImage": "الصورة السابقة",
    "collection.nextImage": "الصورة التالية",
    "collection.shippingInfo": "معلومات الشحن",
    "collection.uaeDelivery": "التوصيل داخل الإمارات",
    "collection.freeShippingOver": "شحن مجاني للطلبات فوق 200 درهم",
    "collection.estimatedDelivery": "التوصيل المتوقع",
    "collection.deliveryTime": "2-5 أيام عمل داخل الإمارات",
    "collection.securePackaging": "تغليف آمن",
    "collection.packagingText": "يتم تغليف جميع الطلبات بعناية",
    "collection.returns14": "إرجاع خلال 14 يوماً",
    "collection.returns14Text": "أعد المنتجات غير المفتوحة خلال 14 يوماً",
    "collection.easyRefunds": "استرداد سهل",
    "collection.easyRefundsText": "استرداد كامل للمرتجعات المؤهلة",
    "collection.signInRequired": "يرجى تسجيل الدخول",
    "collection.signInToCart": "تحتاج إلى تسجيل الدخول لإضافة عناصر إلى سلتك.",
    "collection.sampleMaxTitle": "تم بلوغ الحد الأقصى",
    "collection.sampleMaxDesc": "يمكنك إضافة عينة واحدة فقط لكل منتج.",
    "collection.sampleQtyLimited": "كمية العينة محدودة",
    "collection.sampleQtyDesc": "يُسمح بعينة واحدة فقط لكل منتج.",
    "collection.sample": "عينة",
    "collection.reviewNotAllowed": "لا يمكن كتابة تقييم",
    "collection.reviewPurchasedOnly": "يمكنك تقييم المنتجات التي اشتريتها فقط.",
    "collection.noEligibleOrder": "لا يوجد طلب مؤهل",
    "collection.shippingPolicyUnavailable": "تفاصيل سياسة الشحن غير متاحة بعد.",
    "collection.returnPolicyUnavailable": "تفاصيل سياسة الإرجاع غير متاحة بعد.",

    // Quiz journey (original quiz, results, package selection, personalization)
    "quiz.discoverEssence": "اكتشف جوهرك",
    "quiz.createSignature": "اصنع عطرك المميز",
    "quiz.stepName.question": "لمن هذا العطر؟",
    "quiz.stepName.subtitle": "أدخل اسم الشخص الذي سيضع هذا العطر",
    "quiz.stepGender.question": "اختر الجنس المفضل",
    "quiz.stepGender.subtitle": "يساعدنا هذا في تكييف ملف العطر",
    "quiz.stepAge.question": "ما الفئة العمرية؟",
    "quiz.stepAge.subtitle": "الأعمار المختلفة تتناسب مع نفحات مختلفة",
    "quiz.stepSkin.question": "ما نوع البشرة؟",
    "quiz.stepSkin.subtitle": "كيمياء البشرة تؤثر في تطور العطر",
    "quiz.stepNature.question": "أي بيئة طبيعية تخاطب الروح؟",
    "quiz.stepNature.subtitle": "الطبيعة تلهم جوهر عطرك",
    "quiz.stepPersonality.question": "صف أسلوب الشخصية",
    "quiz.stepPersonality.subtitle": "جوهرك يحدد عطرك",
    "quiz.stepStyle.question": "أسلوب العطر المفضل",
    "quiz.stepStyle.subtitle": "التقليد الذي يوجه إبداعك",
    "quiz.namePlaceholder": "أدخل اسم المستلم...",
    "quiz.countryPlaceholder": "اختر بلد الشحن *",
    "quiz.countryLocked": "البلد مقفل ليطابق طلبك الأول",
    "quiz.continue": "متابعة",
    "quiz.cancelEdit": "إلغاء التعديل",
    "quiz.stepOf": "الخطوة {current} من {total}",
    "quiz.resultsSaved": "نتائجك محفوظة",
    "quiz.codesError": "تعذر إنشاء رموز عطورك. حاول مرة أخرى لمتابعة طلبك المخصص.",
    "quiz.tryAgain": "حاول مرة أخرى",
    "quiz.tryingAgain": "جارٍ المحاولة…",
    "quiz.backToQuiz": "العودة إلى الاختبار",
    "quiz.createdFor": "صُنع لـ {name}",
    "quiz.aiCollection": "مجموعتك المصممة بالذكاء الاصطناعي",
    "quiz.openingFallback": "بناءً على شخصيتك {personality} وحبك لـ {nature}، قمنا بتأليف ثلاثة عطور فريدة بتقليد {style}.",
    "quiz.selectPackage": "اختر باقتك",
    "quiz.bestValue": "أفضل قيمة",
    "quiz.selectPerfume": "اختر عطرك",
    "quiz.packageFallback": "عطر فاخر مصمم بالذكاء الاصطناعي.",
    "quiz.viewPackageInfo": "عرض معلومات عن {name}",
    "quiz.editSelection": "عدّل اختيارك",
    "quiz.chooseExperience": "اختر تجربتك",
    "quiz.loadingPackages": "جارٍ تحميل الباقات...",
    "quiz.saveChanges": "حفظ التغييرات",
    "quiz.selectPackageError": "اختر باقة",
    "quiz.makeItPersonal": "اجعلها شخصية",
    "quiz.personalTouch": "أضف لمستك الشخصية",
    "quiz.personalIntro": "اصنع تجربة هدية لا تُنسى برسالة من القلب.",
    "quiz.personalIntroBox": "اصنع تجربة هدية لا تُنسى برسالة من القلب وصورة اختيارية.",
    "quiz.greetingCard": "رسالة بطاقة التهنئة",
    "quiz.messageLabel": "اكتب رسالة شخصية (اختياري)",
    "quiz.messagePlaceholder": "عزيزي [الاسم]، صُنع هذا العطر خصيصًا لك...",
    "quiz.rephrase": "إعادة صياغة",
    "quiz.rephrasing": "جارٍ إعادة الصياغة…",
    "quiz.toArabic": "إلى العربية",
    "quiz.toEnglish": "إلى الإنجليزية",
    "quiz.translating": "جارٍ الترجمة…",
    "quiz.characters": "{count}/500 حرف",
    "quiz.photoTitle": "إضافة صورة شخصية",
    "quiz.photoSubtitle": "ارفع صورة لتظهر على غطاء صندوق التوقيع (مجانًا).",
    "quiz.uploading": "جارٍ الرفع...",
    "quiz.preservingQuality": "الحفاظ على الجودة الكاملة",
    "quiz.clickToUpload": "اضغط للرفع",
    "quiz.photoFormats": "PNG أو JPG حتى 20MB",
    "quiz.photoHere": "ستظهر صورتك هنا",
    "quiz.fullResolution": "الدقة الكاملة محفوظة للطباعة",
    "quiz.choosePackage": "اختر باقتك",
    "quiz.editing": "تعديل",
    "quiz.phase.quiz": "الاختبار",
    "quiz.phase.package": "الباقة",
    "quiz.phase.personalization": "التخصيص",
    "quiz.phase.checkout": "الدفع",
    "quiz.phase.confirmation": "التأكيد",
    "quiz.signatureBlend": "خلطة مميزة",
    "quiz.perfumeCode": "رمز عطرك",
    "quiz.generating": "جارٍ الإنشاء...",
    "quiz.topNotes": "النفحات العليا",
    "quiz.heartNotes": "نفحات القلب",
    "quiz.baseNotes": "النفحات القاعدية",
    "quiz.noteTop": "المقدمة",
    "quiz.noteHeart": "القلب",
    "quiz.noteBase": "القاعدة",
    "quiz.selected": "تم الاختيار",
    "quiz.typeSensation": "سينسيشن (رسمي)",
    "quiz.typeFreshEnergy": "طاقة منعشة (خارجي)",
    "quiz.typeCasual": "كاجوال (يومي)",
    "quiz.loadingTitle": "نصنع عطورك المميزة",
    "quiz.loadingSubtitle": "يحلل الذكاء الاصطناعي تفضيلاتك ويؤلف ثلاثة عطور فريدة خصيصًا لـ {name}...",
    "quiz.tapToPlay": "اضغط للتشغيل",
    "quiz.clickToPlay": "اضغط للتشغيل",
    "quiz.fileTooLarge": "الملف كبير جدًا",
    "quiz.fileTooLargeDesc": "يرجى اختيار صورة أقل من 20MB",
    "quiz.uploadFailed": "فشل الرفع",
    "quiz.uploadFailedDesc": "يرجى تجربة صورة مختلفة.",
    "quiz.couldntRephrase": "تعذرت إعادة الصياغة",
    "quiz.rephraseRetry": "يرجى المحاولة مرة أخرى بعد قليل.",
    "quiz.rephrased": "تمت إعادة صياغة الرسالة",
    "quiz.rephrasedDesc": "تم تحسين رسالتك بلمسة أدفأ.",
    "quiz.couldntTranslate": "تعذّرت الترجمة",
    "quiz.translatedAr": "تمت الترجمة إلى العربية",
    "quiz.translatedArDesc": "تمت ترجمة رسالتك.",
    "quiz.translatedEn": "تمت الترجمة إلى الإنجليزية",
    "quiz.translatedEnDesc": "تمت ترجمة رسالتك.",
    "quiz.sessionExpired": "انتهت الجلسة",
    "quiz.sessionExpiredDesc": "يرجى بدء الاختبار من جديد.",
    "quiz.orderSaved": "تم حفظ الطلب",
    "quiz.orderSavedBox": "تم حفظ صندوق التوقيع في السلة.",
    "quiz.orderSavedSingle": "تم حفظ الزجاجة المفردة في السلة.",
    "quiz.couldntSaveOrder": "تعذر حفظ الطلب",
    "quiz.storageFullDesc": "مساحة تخزين المتصفح ممتلئة. يرجى المحاولة بعد تفريغ السلة.",
    "quiz.couldntSaveChanges": "تعذر حفظ التغييرات",
    "quiz.photoTooLargeDesc": "الصورة المرفوعة كبيرة جدًا للتخزين. يرجى رفع صورة أصغر والمحاولة مرة أخرى.",
    "quiz.itemUpdated": "تم تحديث العنصر",
    "quiz.itemUpdatedDesc": "تم تحديث عنصر السلة.",
    "quiz.tryAgainDesc": "يرجى المحاولة مرة أخرى.",
    "quiz.somethingWrong": "حدث خطأ ما. يرجى المحاولة مرة أخرى.",
  },
};

export default LanguageProvider;
